import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到" description="抱歉，您访问的文章不存在。" />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">
              {t("back_to_home")}
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={`${article.title} | 猫咪社区`}
        description={article.summary}
        keywords={article.tags.join(",")}
        canonicalUrl={`https://catcommunity.cn/article/${article.id}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.summary,
            author: {
              "@type": "Person",
              name: article.author,
            },
            datePublished: article.date,
            image: article.imageUrl,
            publisher: {
              "@type": "Organization",
              name: "猫咪社区",
            },
          }),
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: t("breadcrumb_home"), href: "/" },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="px-4 md:px-6 pb-14 md:pb-20">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10">
              <article>
                <div className="mb-6">
                  <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 whitespace-nowrap">
                    {article.categoryName}
                  </span>
                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4 leading-tight">
                    {article.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full bg-accent-200 flex items-center justify-center text-accent-700 font-medium text-xs">
                        {article.author.charAt(0)}
                      </span>
                      <span>{article.author}</span>
                    </div>
                    <span className="text-foreground-300">·</span>
                    <span>{article.date}</span>
                    <span className="text-foreground-300">·</span>
                    <span className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-eye-line"></i>
                      </span>
                      {article.readCount.toLocaleString()} {t("read_count")}
                    </span>
                  </div>
                </div>

                <div className="relative h-64 md:h-80 overflow-hidden rounded-lg mb-8">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div
                  className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                    [&_h2]:text-xl [&_h2]:font-heading [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4
                    [&_h3]:text-lg [&_h3]:font-medium [&_h3]:text-foreground-800 [&_h3]:mt-8 [&_h3]:mb-3
                    [&_p]:mb-4 [&_p]:leading-relaxed
                    [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ul]:space-y-1
                    [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4 [&_ol]:space-y-1
                    [&_li]:mb-1
                    [&_strong]:text-foreground-900 [&_strong]:font-medium
                    [&_em]:text-foreground-500"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="mt-10 pt-8 border-t border-background-200">
                  <div className="flex flex-wrap items-center gap-2 mb-6">
                    <span className="text-sm text-foreground-500 mr-2">{t("tags")}：</span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-6 text-sm text-foreground-500">
                    <button className="flex items-center gap-1.5 hover:text-primary-500 transition-colors duration-200">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-heart-line"></i>
                      </span>
                      {article.likeCount} {t("like_count")}
                    </button>
                    <button className="flex items-center gap-1.5 hover:text-primary-500 transition-colors duration-200">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-chat-1-line"></i>
                      </span>
                      {article.commentCount} {t("comment_count")}
                    </button>
                    <button className="flex items-center gap-1.5 hover:text-primary-500 transition-colors duration-200">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-share-forward-line"></i>
                      </span>
                      分享
                    </button>
                  </div>
                </div>
              </article>

              <aside className="hidden lg:block">
                <div className="sticky top-20">
                  <h3 className="text-lg font-heading text-foreground-900 mb-5">
                    {t("related_articles")}
                  </h3>
                  <div className="space-y-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.id}`}
                        className="flex gap-3 group"
                      >
                        <div className="w-20 h-16 flex-shrink-0 rounded-md overflow-hidden">
                          <img
                            src={ra.imageUrl}
                            alt={ra.title}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200 leading-snug">
                            {ra.title}
                          </h4>
                          <p className="text-xs text-foreground-400 mt-1">{ra.date}</p>
                        </div>
                      </Link>
                    ))}
                  </div>

                  <div className="mt-8 p-5 bg-background-100 rounded-lg">
                    <h4 className="text-sm font-medium text-foreground-900 mb-2">关于猫咪社区</h4>
                    <p className="text-xs text-foreground-500 leading-relaxed mb-4">
                      猫咪社区是专为爱猫人士打造的互动平台，汇聚海量猫咪养护经验与萌猫图片。
                    </p>
                    <Link
                      to="/about"
                      className="text-xs text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
                    >
                      了解更多 →
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}